import { useState } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { mdiPlus } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useContractTypes } from '../../hooks/useQueries'
import { AssetPicker } from '../AssetPicker'
import { Can } from '../../contexts/PermissionsContext'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { Card, CardContent } from '../ui/card'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { EmptyState } from '../ui/empty-state'
import { apiClient } from '../../api/client'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { apiErrorMessage } from './assembly-api'

const MINUTES_TYPE_CODE = 'ASSEMBLY_MINUTES'

/**
 * The general assemblies of the organization: one row per minutes (PV). The
 * building filter lives in the URL (`?asset=`), like every list filter.
 * Spec 2026-10-05-01 §8.
 */
export function AssembliesPage() {
    const { org } = useParams({ from: '/$org/assemblies/' })
    const search = useSearch({ from: '/$org/assemblies/' })
    const navigate = useNavigate()
    const { t } = useTranslation('assemblies')
    useBreadcrumbs([{ title: t('common:nav.home'), path: `/${org}` }, { title: t('title') }])

    const { data: contractTypes = [] } = useContractTypes(org)
    const minutesType = contractTypes.find((ct) => ct.code === MINUTES_TYPE_CODE)

    const { data, isLoading } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/contracts',
        {
            params: {
                path: { orgCode: org },
                query: {
                    contract_type_uid: minutesType?.uid,
                    include_governance: true,
                    asset_uid: search.asset,
                    include: 'assets',
                    limit: 100,
                } as never,
            },
        },
        { enabled: !!minutesType },
    )
    // The list answers `{data: [...]}` (the documented `items` is not what the server sends).
    const listed = data as unknown as { data?: unknown[]; items?: unknown[] } | undefined
    const rows = (listed?.data ?? listed?.items ?? []) as Array<{
        uid: string
        code?: string
        name?: string
        status?: string
        flags?: string[]
        assets?: Array<{ asset?: { name?: string } }>
    }>

    const { data: filterAssetData } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}',
        { params: { path: { orgCode: org, assetUid: search.asset ?? '' } } },
        { enabled: !!search.asset },
    )
    const filterAsset = filterAssetData as { name?: string } | undefined

    const [creating, setCreating] = useState(false)

    return (
        <div className="space-y-6" data-testid="assemblies-page">
            <PageHeader
                icon={SECTION_ICONS.assemblies}
                title={t('title')}
                description={t('description')}
                actions={
                    <Can perm="contracts.write">
                        <Button onClick={() => setCreating(true)} disabled={!minutesType} data-testid="assembly-create">
                            <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                            {t('create.action')}
                        </Button>
                    </Can>
                }
            />

            <div className="max-w-md" data-testid="assemblies-filter">
                <Label className="mb-1 block text-sm">{t('filter.building')}</Label>
                <AssetPicker
                    org={org}
                    value={search.asset}
                    valueLabel={filterAsset?.name}
                    testId="assemblies-asset-filter"
                    onChange={(uid) => navigate({ to: '/$org/assemblies', params: { org }, search: { asset: uid }, replace: true })}
                />
                {search.asset && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="mt-1"
                        onClick={() => navigate({ to: '/$org/assemblies', params: { org }, search: { asset: undefined }, replace: true })}
                        data-testid="assemblies-filter-clear"
                    >
                        {t('filter.clear')}
                    </Button>
                )}
            </div>

            {isLoading ? (
                <p className="text-sm text-muted-foreground">{t('loading')}</p>
            ) : rows.length === 0 ? (
                <EmptyState icon={SECTION_ICONS.assemblies} title={t('empty.title')} description={t('empty.description')} data-testid="assemblies-empty" />
            ) : (
                <Card>
                    <CardContent className="divide-y p-0" data-testid="assemblies-list">
                        {rows.map((row) => (
                            <Link
                                key={row.uid}
                                to="/$org/assemblies/$uid"
                                params={{ org, uid: row.uid }}
                                search={{ tab: undefined, resolution: undefined }}
                                className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-muted/50"
                                data-testid={`assembly-row-${row.uid}`}
                            >
                                <div className="min-w-0">
                                    <div className="truncate font-medium">{row.name || row.code}</div>
                                    <div className="truncate text-sm text-muted-foreground">
                                        {row.code} · {(row.assets ?? []).map((a) => a.asset?.name).filter(Boolean).join(', ')}
                                    </div>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                    {row.flags?.includes('to_approve') && <Badge variant="warning">{t('to_approve')}</Badge>}
                                    <Badge variant="outline">{t(`status.${row.status ?? 'draft'}`)}</Badge>
                                </div>
                            </Link>
                        ))}
                    </CardContent>
                </Card>
            )}

            {creating && minutesType && (
                <CreateMinutesDialog
                    org={org}
                    minutesTypeUid={minutesType.uid}
                    defaultAsset={search.asset}
                    onClose={() => setCreating(false)}
                />
            )}
        </div>
    )
}

function CreateMinutesDialog({
    org,
    minutesTypeUid,
    defaultAsset,
    onClose,
}: {
    org: string
    minutesTypeUid: string
    defaultAsset?: string
    onClose: () => void
}) {
    const { t } = useTranslation('assemblies')
    const navigate = useNavigate()
    const [name, setName] = useState('')
    const [assetUid, setAssetUid] = useState(defaultAsset ?? '')
    const [assetName, setAssetName] = useState('')
    const [mode, setMode] = useState<'platform' | 'recorded'>('platform')
    const create = $api.useMutation('post', '/api/v1/organizations/{orgCode}/contracts')
    const error = apiErrorMessage(create.error)

    const submit = async () => {
        if (!name.trim() || !assetUid) return
        const created = await create.mutateAsync({
            params: { path: { orgCode: org } },
            body: {
                contract_type_uid: minutesTypeUid,
                name: name.trim(),
                status: 'draft',
                assets: [{ asset_uid: assetUid }],
            } as never,
        })
        const uid = (created as { uid?: string }).uid
        if (uid) {
            // The mode is a clause (assembly_mode): written right after the creation.
            const { data: list } = await apiClient.GET('/api/v1/organizations/{orgCode}/contract-types/{uid}/properties', {
                params: { path: { orgCode: org, uid: minutesTypeUid } },
            })
            const rows = ((list as { data?: unknown[] } | undefined)?.data ?? []) as { property_definition?: { code?: string; uid?: string } }[]
            const def = rows.find((r) => r.property_definition?.code === 'assembly_mode')?.property_definition?.uid
            if (def) {
                await apiClient.PUT('/api/v1/organizations/{orgCode}/contracts/{uid}/properties/{propertyUid}', {
                    params: { path: { orgCode: org, uid, propertyUid: def } },
                    body: { value: mode },
                })
            }
        }
        if (uid) navigate({ to: '/$org/assemblies/$uid', params: { org, uid }, search: { tab: 'details', resolution: undefined } })
    }

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent data-testid="assembly-create-dialog">
                <DialogHeader>
                    <DialogTitle>{t('create.title')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="assembly-name">{t('create.name')}</Label>
                        <Input
                            id="assembly-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('create.name_placeholder')}
                            data-testid="assembly-create-name"
                        />
                    </div>
                    <div>
                        <Label>{t('create.building')}</Label>
                        <AssetPicker
                            org={org}
                            value={assetUid}
                            valueLabel={assetName}
                            testId="assembly-create-asset"
                            onChange={(uid, label) => {
                                setAssetUid(uid)
                                setAssetName(label)
                            }}
                            help={t('create.building_help')}
                        />
                    </div>
                    <div>
                        <Label htmlFor="assembly-mode">{t('create.mode')}</Label>
                        <select
                            id="assembly-mode"
                            value={mode}
                            onChange={(e) => setMode(e.target.value as 'platform' | 'recorded')}
                            className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                            data-testid="assembly-create-mode"
                        >
                            <option value="platform">{t('create.mode_platform')}</option>
                            <option value="recorded">{t('create.mode_recorded')}</option>
                        </select>
                    </div>
                    {error && <p className="text-sm text-destructive" data-testid="assembly-create-error">{error}</p>}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t('cancel')}</Button>
                    <Button onClick={submit} disabled={!name.trim() || !assetUid || create.isPending} data-testid="assembly-create-submit">
                        {t('create.submit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
